import {
  MVP_ORGANIZATION_ACCOUNT_ROLES,
  MVP_PLATFORM_ACCOUNT_ROLES,
} from '@/types';
import type {
  MvpAccountRole,
  PlatformAccountRole,
  ScopedRelationshipType,
} from '@/types';
import { MVP_ACCOUNT_ROLE_LABELS } from '@/lib/auth/accountRoles';

/**
 * Canonical Demo Persona model (Blueprint-aligned).
 *
 * The primary Demo Role Switcher presents exactly the SIX canonical ACCOUNT
 * roles across the two authority planes. Functional / relationship scenarios
 * Functional / relationship scenarios (delivery lead, capital representative, …) are NOT account roles — they
 * are modelled as explicit scoped OVERLAYS on top of `organization_member`.
 *
 * Legacy functional persona keys (business_admin, workspace_admin, system_admin,
 * …) remain only as demo persona catalog identifiers and are mapped to a
 * canonical account role here; they are never presented as account roles.
 */

/** The six canonical demo account roles. */
export const DEMO_ACCOUNT_ROLES: readonly MvpAccountRole[] = [
  ...MVP_ORGANIZATION_ACCOUNT_ROLES,
  ...MVP_PLATFORM_ACCOUNT_ROLES,
] as const;

export interface DemoAccountRoleGroup {
  readonly id: 'customer' | 'platform';
  readonly label: string;
  readonly description: string;
  readonly roles: readonly MvpAccountRole[];
  readonly dotClass: string;
}

export const DEMO_ACCOUNT_ROLE_GROUPS: readonly DemoAccountRoleGroup[] = [
  {
    id: 'customer',
    label: 'Customer Organization',
    description: 'Canonical organization account roles',
    roles: MVP_ORGANIZATION_ACCOUNT_ROLES,
    dotClass: 'bg-status-neutral',
  },
  {
    id: 'platform',
    label: 'AX1 Control Plane',
    description: 'Canonical platform account roles',
    roles: MVP_PLATFORM_ACCOUNT_ROLES,
    dotClass: 'bg-accent-primary',
  },
] as const;

/** Demo switcher labels — keep in sync with MVP_ACCOUNT_ROLE_LABELS (display only). */
export const DEMO_ACCOUNT_ROLE_LABELS: Readonly<Record<MvpAccountRole, string>> =
  MVP_ACCOUNT_ROLE_LABELS;

/**
 * Canonical account role → representative demo persona catalog key.
 *
 * The persona keys are LEGACY catalog identifiers (see showcaseCatalog). They
 * carry the correct canonical `account_role`, so switching by canonical role
 * mounts the right identity while the persisted key keeps reload deterministic.
 */
const TOKEN_OPS_PERSONA = 'business_admin';

export const DEMO_ACCOUNT_ROLE_PERSONA: Readonly<Record<MvpAccountRole, string>> = {
  organization_owner: 'organization_admin',
  organization_admin: TOKEN_OPS_PERSONA,
  organization_member: 'workspace_member', // account_role: organization_member (+ workspace_member)
  platform_owner: 'platform_owner', // account_role: platform_owner
  platform_admin: 'system_admin', // account_role: platform_admin
  support_operator: 'support_admin', // account_role: support_operator
};

export interface DemoScopedOverlay {
  /** ScopedPersonaKey in the demo catalog. */
  readonly key: string;
  readonly label: string;
  readonly relationship: ScopedRelationshipType;
  readonly resourceLabel: string;
  readonly description: string;
}

/**
 * Scoped-relationship overlays. Each mounts an `organization_member` identity
 * whose access derives ONLY from the named scoped relationship — never from an
 * account-role shortcut.
 */
export const DEMO_SCOPED_OVERLAYS: readonly DemoScopedOverlay[] = [
  {
    key: 'unassigned_member',
    label: 'Unassigned Member',
    relationship: 'workspace_member',
    resourceLabel: 'Workspace only',
    description: 'Workspace Member + workspace membership → Welcome',
  },
  {
    key: 'project_manager',
    label: 'Project Manager',
    relationship: 'project_manager',
    resourceLabel: 'Showcase projects',
    description: 'Workspace Member + project_manager → assigned project',
  },
  {
    key: 'project_contributor',
    label: 'Contributor',
    relationship: 'project_contributor',
    resourceLabel: 'Assigned project',
    description: 'Workspace Member + project_contributor → assigned project',
  },
  {
    key: 'observer',
    label: 'Observer',
    relationship: 'observer',
    resourceLabel: 'Assigned project',
    description: 'Workspace Member + observer → read-only project',
  },
  {
    key: 'validator',
    label: 'Task Validator',
    relationship: 'validator',
    resourceLabel: 'Task “Issue engineer release certificate”',
    description: 'Workspace Member + validator on exact Task (no project-wide role)',
  },
  {
    key: 'validator_gate',
    label: 'Gate Validator',
    relationship: 'validator',
    resourceLabel: 'Gate / Milestone 1',
    description: 'Workspace Member + validator on exact Gate (no project-wide role)',
  },
  {
    key: 'validator_milestone',
    label: 'Milestone Validator',
    relationship: 'validator',
    resourceLabel: 'Milestone 1',
    description: 'Workspace Member + validator on exact Milestone (no project-wide role)',
  },
  {
    key: 'reporting_recipient',
    label: 'Reporting Recipient',
    relationship: 'reporting_recipient',
    resourceLabel: 'Assigned report',
    description: 'Workspace Member + reporting_recipient → Reports',
  },
  {
    key: 'decision_participant',
    label: 'Decision Participant',
    relationship: 'decision_participant',
    resourceLabel: 'Assigned decision',
    description: 'Workspace Member + decision_participant → Decision',
  },
  {
    key: 'company_representative',
    label: 'Investee Representative',
    relationship: 'company_representative',
    resourceLabel: 'Meridian Advisory (Investee on Harbour District)',
    description:
      'Workspace Member + company_representative → Investee Portal in Harbour District context (Company Portal fallback without project participation)',
  },
  {
    key: 'partner_representative',
    label: 'Partner Representative',
    relationship: 'partner_representative',
    resourceLabel: 'Meridian Advisory (Partner Portal)',
    description: 'Workspace Member + partner_representative → Partner Portal',
  },
  {
    key: 'vendor_representative',
    label: 'Vendor Representative',
    relationship: 'vendor_representative',
    resourceLabel: 'Atlas Delivery (Vendor Portal)',
    description: 'Workspace Member + vendor_representative → Vendor Portal',
  },
  {
    key: 'investor_representative',
    label: 'Investor Representative',
    relationship: 'investor_representative',
    resourceLabel: 'Assigned investment',
    description: 'Workspace Member + investor_representative → Investor Portal',
  },
  {
    key: 'investment_manager',
    label: 'Investment Manager',
    relationship: 'investment_manager',
    resourceLabel: 'Assigned investment',
    description: 'Workspace Member + investment_manager → managed Investor Portal surfaces',
  },
];

export type DemoPersonaKey = MvpAccountRole | (typeof DEMO_SCOPED_OVERLAYS)[number]['key'];

const DEMO_ACCOUNT_ROLE_SET = new Set<string>(DEMO_ACCOUNT_ROLES);
const DEMO_SCOPED_PERSONA_KEY_SET = new Set<string>(
  DEMO_SCOPED_OVERLAYS.map((overlay) => overlay.key),
);
const DEMO_PERSONA_COMPATIBILITY_KEYS = new Map<string, MvpAccountRole>(
  Object.entries(DEMO_ACCOUNT_ROLE_PERSONA).map(([role, personaKey]) => [personaKey, role as MvpAccountRole]),
);

/**
 * Normalizes only canonical Demo personas and explicitly supported historical
 * fixture keys. It deliberately rejects arbitrary legacy role labels.
 */
export function normalizeDemoPersonaKey(value: string | null | undefined): DemoPersonaKey | null {
  if (!value) return null;
  const key = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (DEMO_ACCOUNT_ROLE_SET.has(key) || DEMO_SCOPED_PERSONA_KEY_SET.has(key)) {
    return key as DemoPersonaKey;
  }
  return DEMO_PERSONA_COMPATIBILITY_KEYS.get(key) ?? null;
}

const PLATFORM_ROLE_SET: ReadonlySet<string> = new Set<string>(MVP_PLATFORM_ACCOUNT_ROLES);

export function isDemoPlatformAccountRole(role: MvpAccountRole): role is PlatformAccountRole {
  return PLATFORM_ROLE_SET.has(role);
}

/**
 * Landing route after selecting a canonical account role. Platform roles go to
 * the Control Plane; customer roles resolve through HomeRedirect (onboarding /
 * workspace resolution / dashboard) rather than a hard-coded dashboard.
 */
export function getDemoAccountRoleLandingRoute(role: MvpAccountRole): string {
  return isDemoPlatformAccountRole(role) ? '/platform' : '/';
}
